import { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { Shell } from '@/components/layout/Shell';
import { MeridianShell } from '@/components/layout/MeridianShell';
import { useAuth } from '@/hooks/useAuth';
import { IS_DEMO_MODE } from '@/api/demoMode';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const MapPage = lazy(() => import('@/pages/MapPage'));
const FieldsPage = lazy(() => import('@/pages/FieldsPage'));
const AlertsPage = lazy(() => import('@/pages/AlertsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const MeridianConditionPage = lazy(() => import('@/pages/meridian/MeridianConditionPage'));
const MeridianOverviewPage = lazy(() => import('@/pages/meridian/MeridianOverviewPage'));
const MeridianBriefingPage = lazy(() => import('@/pages/meridian/MeridianBriefingPage'));
const MeridianProgressPage = lazy(() => import('@/pages/meridian/MeridianProgressPage'));
const MeridianYieldPage = lazy(() => import('@/pages/meridian/MeridianYieldPage'));
const MeridianMapPage = lazy(() => import('@/pages/meridian/MeridianMapPage'));
const MeridianPredictionsPage = lazy(() => import('@/pages/meridian/MeridianPredictionsPage'));
const MeridianSurveyPage = lazy(() => import('@/pages/meridian/MeridianSurveyPage'));
const MeridianBaselinesPage = lazy(() => import('@/pages/meridian/MeridianBaselinesPage'));
const MeridianExportsPage = lazy(() => import('@/pages/meridian/MeridianExportsPage'));
const MeridianOpsPage = lazy(() => import('@/pages/meridian/MeridianOpsPage'));
const MeridianSettingsPage = lazy(() => import('@/pages/meridian/MeridianSettingsPage'));
const MeridianClassificationCountyPage = lazy(() => import('@/pages/meridian/MeridianClassificationCountyPage'));
const MeridianClassificationParcelPage = lazy(() => import('@/pages/meridian/MeridianClassificationParcelPage'));
const MeridianClassificationAccuracyPage = lazy(() => import('@/pages/meridian/MeridianClassificationAccuracyPage'));

function PageSkeleton() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <div className="scanline-loader h-7 w-40 rounded-full border border-canopy-100" aria-hidden="true" />
        <span className="text-sm">Loading…</span>
      </div>
    </div>
  );
}

function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // The demo snapshot only serves a subset of Meridian surfaces (see
  // DEMO_NAV_ORDER in MeridianShell). Other routes 404 against the static
  // server, so redirect stray URLs to the landing view; allowed routes render
  // without the auth gate (the demo runs on a fixed public session).
  if (IS_DEMO_MODE && location.pathname.startsWith('/meridian/')) {
    const DEMO_MERIDIAN_PATHS = [
      '/meridian/overview',
      '/meridian/briefing',
      '/meridian/yield',
      '/meridian/classification',
      '/meridian/survey',
      '/meridian/condition',
      '/meridian/map',
      '/meridian/baselines',
    ];
    if (!DEMO_MERIDIAN_PATHS.includes(location.pathname)) {
      return <Navigate to="/meridian/overview" replace />;
    }
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <PageSkeleton />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
}

function GuestOnly() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <PageSkeleton />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={IS_DEMO_MODE ? '/meridian/overview' : '/app/dashboard'} replace />,
  },
  {
    element: <GuestOnly />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<div className="min-h-screen" />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/signup',
        element: (
          <Suspense fallback={<div className="min-h-screen" />}>
            <SignupPage />
          </Suspense>
        ),
      },
    ],
  },
  { path: '/verify-email', element: <Suspense fallback={<div className="min-h-screen" />}><VerifyEmailPage /></Suspense> },
  { path: '/forgot-password', element: <Suspense fallback={<div className="min-h-screen" />}><ForgotPasswordPage /></Suspense> },
  { path: '/reset-password', element: <Suspense fallback={<div className="min-h-screen" />}><ResetPasswordPage /></Suspense> },
  {
    path: '/app',
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <Navigate to="/app/dashboard" replace /> },
          { path: 'dashboard', element: <Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense> },
          { path: 'map', element: <Suspense fallback={<PageSkeleton />}><MapPage /></Suspense> },
          { path: 'fields', element: <Suspense fallback={<PageSkeleton />}><FieldsPage /></Suspense> },
          { path: 'alerts', element: <Suspense fallback={<PageSkeleton />}><AlertsPage /></Suspense> },
          { path: 'reports', element: <Suspense fallback={<PageSkeleton />}><ReportsPage /></Suspense> },
          { path: 'settings', element: <Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense> },
        ],
      },
    ],
  },
  {
    path: '/meridian',
    element: <RequireAuth />,
    children: [
      {
        element: <MeridianShell />,
        children: [
          { index: true, element: <Navigate to={IS_DEMO_MODE ? '/meridian/overview' : '/meridian/predictions'} replace /> },
          { path: 'overview', element: <Suspense fallback={<PageSkeleton />}><MeridianOverviewPage /></Suspense> },
          { path: 'briefing', element: <Suspense fallback={<PageSkeleton />}><MeridianBriefingPage /></Suspense> },
          { path: 'predictions', element: <Suspense fallback={<PageSkeleton />}><MeridianPredictionsPage /></Suspense> },
          { path: 'survey', element: <Suspense fallback={<PageSkeleton />}><MeridianSurveyPage /></Suspense> },
          { path: 'condition', element: <Suspense fallback={<PageSkeleton />}><MeridianConditionPage /></Suspense> },
          { path: 'progress', element: <Suspense fallback={<PageSkeleton />}><MeridianProgressPage /></Suspense> },
          { path: 'yield', element: <Suspense fallback={<PageSkeleton />}><MeridianYieldPage /></Suspense> },
          { path: 'map', element: <Suspense fallback={<PageSkeleton />}><MeridianMapPage /></Suspense> },
          { path: 'classification', element: <Suspense fallback={<PageSkeleton />}><MeridianClassificationCountyPage /></Suspense> },
          { path: 'classification/parcel', element: <Suspense fallback={<PageSkeleton />}><MeridianClassificationParcelPage /></Suspense> },
          { path: 'classification/accuracy', element: <Suspense fallback={<PageSkeleton />}><MeridianClassificationAccuracyPage /></Suspense> },
          { path: 'baselines', element: <Suspense fallback={<PageSkeleton />}><MeridianBaselinesPage /></Suspense> },
          { path: 'exports', element: <Suspense fallback={<PageSkeleton />}><MeridianExportsPage /></Suspense> },
          { path: 'ops', element: <Suspense fallback={<PageSkeleton />}><MeridianOpsPage /></Suspense> },
          { path: 'settings', element: <Suspense fallback={<PageSkeleton />}><MeridianSettingsPage /></Suspense> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: IS_DEMO_MODE ? (
      <Navigate to="/meridian/overview" replace />
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-7xl font-black text-earth-200">404</p>
          <h1 className="text-xl font-bold text-gray-900 mt-4">Page not found</h1>
          <p className="text-sm text-gray-500 mt-1 mb-6">The page you&apos;re looking for doesn&apos;t exist.</p>
          <a
            href="/app/dashboard"
            className="inline-flex items-center gap-2 rounded-lg bg-earth-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-earth-700 transition-colors"
          >
            Go to dashboard
          </a>
        </div>
      </div>
    ),
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
